import { GroupConfig } from "./groupConfigs";
import { AutoUpdateVersionPolicy, InstallerBuildMode } from "./installers";
import { License } from "./licenses";
import { Paginated } from "./paginated";
import { Platform } from "../../platforms";

export enum ApprovementStatus {
  Approved = "Approved",
  Pending = "Pending",
  Denied = "Denied",
}

export enum EndpointStatus {
  Approved = "Approved",
  Pending = "Pending",
  Denied = "Denied",
  Revoked = "Revoked",
  Expired = "Expired",
}

export enum EndpointEventStatus {
  Progress = "Progress",
  Failed = "Failed",
  Succeeded = "Succeeded",
}

export enum CoreSensitivity {
  Low = "Low",
  Medium = "Medium",
  High = "High",
}

export enum EndpointLogLevel {
  Debug = "Debug",
  Information = "Information",
  Warning = "Warning",
  Error = "Error",
  Critical = "Critical",
}

export enum EndpointConfigType {
  Group = "Group",
  Endpoint = "Endpoint",
}

export enum ServiceName {
  Core = "Core",
  Scutum = "Scutum",
  Agr = "Agr",
  Coordinator = "Coordinator",
  EaseFilter = "EaseFilter",
}

export enum ServiceStatus {
  Installed = "Installed",
  NotInstalled = "NotInstalled",
  InstallationInProgress = "InstallationInProgress",
  InstallationPending = "InstallationPending",
  UninstallationInProgress = "UninstallationInProgress",
  UninstallationPending = "UninstallationPending",
}

export type Endpoint = {
  id: string;
  name: string;
  aggerVersion: string;
  publicIpCountryCode: string | null;
  operatingSystem: string;
  localIpAddresses: [string, string];
  publicIp: string | null;
  status?: EndpointStatus;
  approvementStatus: ApprovementStatus;
  lastSeen: string;
  coreRunning: boolean;
  online: boolean;
  driverActive: boolean;
  isCanaryActive: boolean;
  isStartupGuardActive: boolean;
  isBaselineActive: boolean;
  company: {
    id: string;
    name: string;
  };
  license: {
    id: string;
    name: string;
    revoked: boolean;
    expired: boolean;
    expirationDate: string;
  };
  eppSoftware: {
    name: string;
  } | null;
  configType: EndpointConfigType;
  platform: Platform;
  groupConfig: {
    id: string;
    name: string;
  } | null;
};

export type EndpointsResponse = Paginated<Endpoint>;

export type EndpointResponse = {
  data: SingleEndpoint;
  success: boolean;
};

export type SingleEndpoint = {
  id: string;
  name: string;
  machineGuid: string;
  processorId: string;
  biosSerialNumber: string;
  company: { id: string; name: string };
  domainName: string;
  aggerVersion: string;
  aggerBuildMode: string;
  isAggerBuildProtected: boolean;
  isCanaryActive: boolean;
  isStartupGuardActive: boolean;
  isBaselineActive: boolean;
  aggerProductCode: string;
  aggerInstallDate: string;
  isAutoUpdateAllowed: boolean;
  autoUpdateBlockedUntil: string | null;
  operatingSystem: string;
  operatingSystemVersion: string;
  operatingSystemLanguage: string | null;
  operatingSystemArchitecture: string | null;
  operatingSystemInstallDate: string | null;
  operatingSystemLastBootUpTime: string | null;
  installedHotFixes: string[];
  configType: string;
  platform: Platform;
  status?: EndpointStatus;
  approvementStatus: ApprovementStatus;
  protectedDirectoryStatus: EndpointEventStatus;
  canaryFileStatuses: CanaryFileStatus[];
  license: License | undefined;
  coreRunning: boolean;
  online: boolean;
  isWindowsInTestMode: boolean;
  driverActive: boolean;
  cpuName: string;
  model: string | null;
  manufacturer: string | null;
  smbiosUuid: string | null;
  totalCores: number;
  totalThreads: number;
  totalRamSizeInGb: number;
  macAddress: string;
  localGateway: string;
  localSubnet: string;
  localIpAddresses: string[];
  publicIp: string | null;
  publicIpCountryCode: string | null;
  publicIpCityName: string | null;
  lastSeen: string;
  eppSoftware: {
    name: string;
  } | null;
  createdAt: string;
  updatedAt: string;
};

export type EndpointConfig = {
  configId: string;
  groupConfig?: GroupConfig;
  isAutoUpdateEnabled: boolean;
  autoUpdateBuildMode: InstallerBuildMode;
  isAutoUpdateProtectedBuildEnabled: boolean;
  autoUpdateVersionPolicy: AutoUpdateVersionPolicy;
  specificAutoUpdateVersion?: string;
  isQuarantineEnabled: boolean;
  isStartupGuardEnabled: boolean;
  isCoreEnabled: boolean;
  isCanaryEnabled: boolean;
  isBaselineEnabled: boolean;
  isMemoryDumpEnabled: boolean;
  isProcessProtectionEnabled: boolean;
  isRegistryProtectionEnabled: boolean;
  isFileSystemProtectionEnabled: boolean;
  isTamperEventReportingEnabled: boolean;
  isAppListCollectionEnabled: boolean;
  isLogCollectionEnabled: boolean;
  minLogLevel: EndpointLogLevel;
  hashPurgingTimeoutHours: number;
  heartbeatIntervalSeconds: number;
  protectedDirectories: string[];
  allowedPaths: string[];
  tamperReportingExcludedPaths: string[];
  allowedRemoteIps: string[];
  canaryFiles: CanaryFile[];
  coreSensitivity: CoreSensitivity;
  isSmbMonitoringEnabled: boolean;
  isSmbLockdownEnabled: boolean;
  firewallRuleTimeoutMinutes: number | null;
  isAggerServicesHidingEnabled: boolean;
  isEnhancedMemoryProtectionEnabled: boolean;
};

export type EndpointConfigResponse = {
  data: EndpointConfig;
  success: boolean;
};

export type CanaryFileStatus = {
  name: string;
  status: EndpointEventStatus;
};

export type CanaryFile = {
  filePath: string;
  isHidden: boolean;
  isSystem: boolean;
  isRandomized: boolean;
};

export type EndpointLocationsResponse = {
  data: string[];
  success: boolean;
};

export type EndpointOperatingSystemsResponse = {
  data: string[];
  success: boolean;
};

export type EndpointAggerVersionsResponse = {
  data: string[];
  success: boolean;
};

export type EndpointGroupConfigsResponse = {
  data: {
    id: string;
    name: string;
  }[];
  success: boolean;
};

export type EndpointApplication = {
  id: string;
  endpoint: {
    id: string;
    name: string;
  };
  name: string;
  version: string;
  installLocation: string;
  publisher: string;
  installedAt: string;
  uninstallString: string;
  estimatedSize: number;
  displayIcon: string;
  helpLink: string;
  urlInfoAbout: string;
  comments: string;
  modifyPath: string;
  readme: string;
  language: string;
  contact: string;
  histories: {
    status: string;
    date: string;
  }[];
};

export type EndpointApplicationsResponse = Paginated<EndpointApplication>;

export type EndpointApplicationResponse = {
  data: EndpointApplication;
  success: boolean;
};

export type EndpointMaintenanceKeyResponse = {
  data: {
    maintenanceKey: string;
  };
  success: boolean;
};

export type EndpointService = {
  name: ServiceName;
  type: string;
  status: ServiceStatus | null;
  version: string;
  isInstalled: boolean;
  isRunning: boolean;
  isActive: boolean;
};

export type EndpointServicesResponse = Paginated<EndpointService>;

export enum EndpointSkipReason {
  NotFound = "NotFound",
  NoAccess = "NoAccess",
  PlatformMismatch = "PlatformMismatch",
  CompanyMismatch = "CompanyMismatch",
}

export type SkippedEndpoint = {
  endpointId: string;
  reason: EndpointSkipReason;
};

export type AssignGroupConfigResponse = {
  data: {
    assignedCount: number;
    skipped: SkippedEndpoint[];
  };
  success: boolean;
};
